import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../../firebase';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  limit, 
  getDocs,
  doc,
  updateDoc,
  startAfter,
  getDoc,
  setDoc
} from 'firebase/firestore';
import { serverTimestamp } from 'firebase/firestore';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const NOTIFICATIONS_PER_PAGE = 10;

const formatDate = (timestamp) => {
  if (!timestamp) return '';
  const date = timestamp.toDate();
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true
  });
};

const UserNotification = ({ isOpen, onClose, unreadCount }) => {
  const { currentUser } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const lastDocRef = useRef(null);
  const [hasMore, setHasMore] = useState(true);

  const fetchNotifications = useCallback(async (isInitial = false) => {
    if (!currentUser) {
      console.log('No current user, skipping notification fetch.');
      setLoading(false);
      return;
    }

    try {
      console.log(`Fetching notifications... (Initial: ${isInitial})`);
      setLoading(true);

      const notificationsRef = collection(db, 'notifications');
      let q = query(
        notificationsRef,
        where('userId', 'in', [currentUser.uid, 'all']),
        where('dismissed', '==', false),
        orderBy('createdAt', 'desc'),
        limit(NOTIFICATIONS_PER_PAGE + 1)
      );

      if (!isInitial && lastDocRef.current) {
        console.log('Fetching next page starting after:', lastDocRef.current.id);
        q = query(
          notificationsRef,
          where('userId', 'in', [currentUser.uid, 'all']),
          where('dismissed', '==', false),
          orderBy('createdAt', 'desc'),
          startAfter(lastDocRef.current),
          limit(NOTIFICATIONS_PER_PAGE + 1)
        );
      } else if (!isInitial && !lastDocRef.current) {
           console.log('Attempted to load more but no lastDoc found, stopping fetch.');
           setLoading(false);
           setHasMore(false);
           return;
      }

      const querySnapshot = await getDocs(q);
      const rawDocs = querySnapshot.docs;
      const hasNextPage = rawDocs.length === NOTIFICATIONS_PER_PAGE + 1;
      const docsToProcess = hasNextPage ? rawDocs.slice(0, NOTIFICATIONS_PER_PAGE) : rawDocs;
      const fetchedNotifications = docsToProcess.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      const notificationIds = fetchedNotifications.filter(n => n.userId === 'all').map(n => n.id);
      const userStatusMap = {};

      if (notificationIds.length > 0 && currentUser?.uid) {
          const statusPromises = notificationIds.map(async (notificationId) => {
              const statusDocRef = doc(db, 'users', currentUser.uid, 'notificationStatus', notificationId);
              const statusDoc = await getDoc(statusDocRef);
              if (statusDoc.exists()) {
                  userStatusMap[notificationId] = statusDoc.data();
              }
          });
          await Promise.all(statusPromises);
      }

      const processedNotifications = fetchedNotifications.map(notification => {
          if (notification.userId === 'all') {
              const status = userStatusMap[notification.id] || {};
              if (!status.dismissed) {
                 return { ...notification, ...status };
              }
              return null;
          } else {
              return notification;
          }
      }).filter(notification => notification !== null);

      if (isInitial) {
        setNotifications(processedNotifications);
      } else {
        setNotifications(prev => [...prev, ...processedNotifications]);
      }

      if (rawDocs.length > 0) {
         lastDocRef.current = rawDocs[rawDocs.length - 1];
      } else {
         lastDocRef.current = null;
      }

      setHasMore(hasNextPage);

      console.log(`Fetched ${processedNotifications.length} non-dismissed notifications. Has more: ${hasNextPage}`);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    if (isOpen) {
      console.log('Modal opened, fetching initial notifications.');
      lastDocRef.current = null;
      setNotifications([]);
      fetchNotifications(true);
    } else {
      console.log('Modal closed.');
    }
  }, [isOpen, fetchNotifications]);

  useEffect(() => {
     console.log('UserNotification received unreadCount prop:', unreadCount);
  }, [unreadCount]);

  const markAsRead = async (notification) => {
    if (!currentUser) return;

    try {
      console.log(`Attempting to mark notification ${notification.id} as read.`);

      if (notification.userId === currentUser.uid) {
        if (!notification.read) {
            const notificationRef = doc(db, 'notifications', notification.id);
            await updateDoc(notificationRef, { read: true });
             setNotifications(prev =>
                prev.map(n =>
                  n.id === notification.id ? { ...n, read: true } : n
                )
            );
            console.log(`User-specific notification ${notification.id} marked as read.`);
        } else {
             console.log(`User-specific notification ${notification.id} already read.`);
        }

      } else if (notification.userId === 'all') {
        const statusDocRef = doc(db, 'users', currentUser.uid, 'notificationStatus', notification.id);
        const statusDoc = await getDoc(statusDocRef);

        if (statusDoc.exists() && statusDoc.data().read) {
            console.log(`Global notification ${notification.id} already marked as read by user ${currentUser.uid}.`);
        } else {
            await setDoc(statusDocRef, { read: true, dismissed: statusDoc.data()?.dismissed || false,  readAt: serverTimestamp() }, { merge: true });
            setNotifications(prev =>
                prev.map(n =>
                  n.id === notification.id ? { ...n, read: true } : n
                )
            );
            console.log(`Global notification ${notification.id} marked as read for user ${currentUser.uid}.`);
        }
      }

    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const dismissNotification = async (notification) => {
    if (!currentUser) return;

    try {
      console.log(`Attempting to dismiss notification ${notification.id}.`);

      if (notification.userId === currentUser.uid) {
         if (!notification.dismissed) {
            const notificationRef = doc(db, 'notifications', notification.id);
            await updateDoc(notificationRef, { dismissed: true });
            setNotifications(prev =>
              prev.filter(n => n.id !== notification.id)
            );
            console.log(`User-specific notification ${notification.id} dismissed.`);
         } else {
            console.log(`User-specific notification ${notification.id} already dismissed.`);
         }

      } else if (notification.userId === 'all') {
        const statusDocRef = doc(db, 'users', currentUser.uid, 'notificationStatus', notification.id);
        const statusDoc = await getDoc(statusDocRef);

        if (statusDoc.exists() && statusDoc.data().dismissed) {
             console.log(`Global notification ${notification.id} already dismissed by user ${currentUser.uid}.`);
        } else {
            await setDoc(statusDocRef, { dismissed: true, read: statusDoc.data()?.read || false, dismissedAt: serverTimestamp() }, { merge: true });
            setNotifications(prev =>
              prev.filter(n => n.id !== notification.id)
            );
            console.log(`Global notification ${notification.id} dismissed for user ${currentUser.uid}.`);
        }
      }

    } catch (error) {
      console.error('Error dismissing notification:', error);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'certificate':
        return '🎓';
      case 'welcome':
        return '👋';
      case 'onboarding':
        return '🚀';
      case 'admin':
        return '📢';
      default:
        return '📌';
    }
  };

  const handleLoadMore = () => {
     console.log('Load more button clicked.');
     fetchNotifications(false);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="fixed right-4 top-16 w-96 max-w-xs sm:max-w-sm md:max-w-md max-h-[80vh] bg-white rounded-lg shadow-xl z-50 overflow-hidden"
        >
          <div className="bg-[rgb(130,88,18)] text-white p-4 flex justify-between items-center">
            <h3 className="font-semibold">Notifications ({unreadCount} unread)</h3>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 text-lg leading-none"
              aria-label="Close notifications"
            >
              ✕
            </button>
          </div>

          <div className="overflow-y-auto max-h-[calc(80vh-4rem)]">
            {loading && notifications.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                Loading notifications...
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                No notifications yet
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {notifications.map((notification) => (
                  <motion.div
                    key={notification.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    onClick={() => markAsRead(notification)}
                    className={`p-3 sm:p-4 hover:bg-gray-50 transition-colors cursor-pointer ${
                       !notification.read ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <span className="text-xl sm:text-2xl flex-shrink-0" role="img" aria-label={notification.type}>
                        {getNotificationIcon(notification.type)}
                      </span>
                      <div className="flex-1 overflow-hidden">
                        <p className="text-sm sm:text-base font-medium text-gray-900 truncate">
                          {notification.title}
                        </p>
                        <div className="text-xs sm:text-sm text-gray-600 mt-1 break-words markdown-container">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {notification.message}
                          </ReactMarkdown>
                        </div>
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mt-2 gap-2 sm:gap-0">
                          <span className="text-xs text-gray-500 flex-shrink-0">
                            {formatDate(notification.createdAt)}
                          </span>
                          {notification.userId === currentUser?.uid && !notification.dismissed && (
                            <div className="flex gap-2">
                              {!notification.read && (
                                <button
                                  onClick={(e) => {
                                      e.stopPropagation();
                                      markAsRead(notification);
                                  }}
                                  className="text-xs text-blue-600 hover:text-blue-800"
                                >
                                  Mark as read
                                </button>
                              )}
                              <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    dismissNotification(notification);
                                }}
                                className="text-xs text-red-600 hover:text-red-800"
                              >
                                Dismiss
                              </button>
                            </div>
                          )}
                          {notification.userId === 'all' && !notification.dismissed && currentUser?.uid && (
                            <div className="flex gap-2">
                                {!notification.read && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            markAsRead(notification);
                                        }}
                                        className="text-xs text-blue-600 hover:text-blue-800"
                                    >
                                        Mark as read
                                    </button>
                                )}
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        dismissNotification(notification);
                                    }}
                                    className="text-xs text-red-600 hover:text-red-800"
                                >
                                    Dismiss
                                </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}

            {hasMore && !loading && (
              <div className="p-4 text-center">
                <button
                  onClick={handleLoadMore}
                  className="text-sm text-[rgb(130,88,18)] hover:text-[rgb(110,68,0)]"
                >
                  Load more
                </button>
              </div>
            )}
             {loading && notifications.length > 0 && (
                <div className="p-4 text-center text-gray-500 text-sm">
                    Loading more...
                </div>
             )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default UserNotification;
